import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchProjectTasks,
  exportProjectTasks,
  reorderTask,
  updateTasksProjectOrder,
  setFilters,
  setSearchFilter,
  resetState,
  selectProjectTasks,
  selectProjectTasksLoading,
  selectCanFetchtMoreTasks,
  selectTotalCount,
  selectProjectTasksTableFilters,
  selectHasProjectTasksTableFilters,
} from 'state/ProjectTasksList/projectTasksListSlice';
import { fetchTaskTypes } from 'state/TasksList/allTasks/allTasksSlice';
import {
  setShouldRefreshTasks,
  selectShouldRefreshTasks,
  updateProjectTaskOrder,
  setSnackBarNotification,
} from 'state/ProjectTasksList/actions/actionsSlice';
import { resetSelectedTasks } from 'state/ProjectTasksList/export/exportSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import {
  fetchProjectProgress,
  resetState as resetProjectProgress,
} from 'state/Project/progress/progressSlice';
import { TaskListFilters } from 'types/store/tasksList';
import { SLICE_STATUS } from 'utils/constants';
import {
  extractFilenameFromContentDisposition,
  createDownloadLinkAndDownloadFile,
} from 'utils/csvExports';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { PersistentFilterTypeKeys } from 'utils/types/filters';

interface UseProjectTasksTableConfig {
  tableType: PersistentFilterTypeKeys;
  supportsReordering?: boolean;
  userId?: string;
}

export const useProjectTasksTable = ({
  tableType,
  supportsReordering = false,
  userId,
}: UseProjectTasksTableConfig) => {
  const dispatch = useAppThunkDispatch();
  const { newTaskTypes = false } = useFlags();
  const displayLoaderWhenFetching = useRef<boolean>(true);
  const { projectId } = useParams<{ projectId: string }>();

  const tasks = useSelector(selectProjectTasks);
  const loading = useSelector(selectProjectTasksLoading);
  const canFetchMoreTasks = useSelector(selectCanFetchtMoreTasks);
  const totalCount = useSelector(selectTotalCount);
  const filters = useSelector(selectProjectTasksTableFilters);
  const shouldRefreshTasks = useSelector(selectShouldRefreshTasks);
  const hasAppliedFilters = useSelector(selectHasProjectTasksTableFilters);

  const {
    SnackbarNotification,
    isNotificationVisible,
    snackbarProps,
    showNotification,
  } = useSnackbarNotification();

  const init = useCallback(
    async (projectId: string, userId?: string) => {
      await dispatch(fetchFilterSettingByType(tableType));

      const fetchParams = {
        projectId,
        userId, // Always pass userId, even if undefined
        includeTotalCount: true,
      };

      dispatch(fetchProjectTasks(fetchParams));
      dispatch(fetchTaskTypes(newTaskTypes));
      dispatch(
        fetchProjectProgress({ projectId, ...(userId ? { userId } : {}) })
      );
    },
    [dispatch, tableType, newTaskTypes]
  );

  // Effects
  useEffect(() => {
    if (projectId) {
      init(projectId, userId);
    }
    return () => {
      dispatch(resetState());
      dispatch(resetSelectedTasks());
      dispatch(resetProjectProgress());
    };
  }, [userId, projectId, init, dispatch]);

  useEffect(() => {
    if (shouldRefreshTasks) {
      dispatch(setShouldRefreshTasks(false));
      init(projectId, userId);
    }
  }, [shouldRefreshTasks, init, dispatch, userId, projectId]);

  useEffect(() => {
    if (
      loading &&
      loading !== SLICE_STATUS.LOADING &&
      !displayLoaderWhenFetching.current
    ) {
      displayLoaderWhenFetching.current = true;
    }
  }, [loading]);

  // Handlers
  const handleFetchMoreTasks = useCallback(() => {
    displayLoaderWhenFetching.current = false;
    const fetchParams = {
      projectId,
      userId, // Always pass userId, even if undefined
      fetchNext: true,
    };
    dispatch(fetchProjectTasks(fetchParams));
  }, [dispatch, projectId, userId]);

  const handleReorderTask = useCallback(
    async (taskId: string, startIndex: number, endIndex: number) => {
      if (!supportsReordering) {
        return;
      }

      const task = tasks[endIndex];
      const newOrder = parseInt(task.projectOrder, 10);

      if (isNaN(newOrder)) {
        dispatch(
          setSnackBarNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get(
              'TASKS.NOTIFICATIONS.REORDER_FAILED.TITLE'
            ),
            notificationMessage: intl.get(
              'TASKS.NOTIFICATIONS.REORDER_FAILED.MESSAGE'
            ),
          })
        );
        return;
      }

      dispatch(reorderTask({ taskId, startIndex, endIndex }));

      try {
        await dispatch(
          updateProjectTaskOrder({
            projectId,
            taskId,
            newOrder: parseInt(task.projectOrder),
          })
        );
        dispatch(updateTasksProjectOrder({ startIndex, endIndex, newOrder }));
      } catch (error) {
        dispatch(
          reorderTask({ taskId, startIndex: endIndex, endIndex: startIndex })
        );
      }
    },
    [dispatch, projectId, tasks, supportsReordering]
  );

  const handleUpdateFilters = useCallback(
    (updatedFilters: TaskListFilters) => {
      dispatch(setFilters(updatedFilters));
      const fetchParams = {
        projectId,
        userId, // Always pass userId, even if undefined
        fetchNext: false,
        includeTotalCount: true,
      };
      dispatch(fetchProjectTasks(fetchParams));
    },
    [dispatch, projectId, userId]
  );

  const handleSearchFilterChange = useCallback(
    (value: string) => {
      dispatch(setSearchFilter(value));
      const fetchParams = {
        projectId,
        userId,
        fetchNext: false,
        includeTotalCount: true,
      };
      dispatch(fetchProjectTasks(fetchParams));
    },
    [dispatch, projectId, userId]
  );

  const handleExportTasks = useCallback(async () => {
    try {
      const exportParams = {
        projectId,
        userId,
      };

      const response = await dispatch(
        exportProjectTasks(exportParams)
      ).unwrap();
      const filename = extractFilenameFromContentDisposition(
        response?.headers['content-disposition']
      );
      createDownloadLinkAndDownloadFile(response?.data, filename);
    } catch (error) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get('TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.TITLE'),
        notificationMessage: intl.get(
          'TASKS_LIST_PAGE.EXPORT_TASKS_ERROR.MESSAGE'
        ),
      });
    }
  }, [projectId, userId, dispatch, showNotification]);

  const refreshTasks = useCallback(() => {
    dispatch(setShouldRefreshTasks(true));
  }, [dispatch]);

  return {
    tasks,
    canFetchMoreTasks,
    totalCount,
    filters,
    isLoading:
      (!loading || loading === SLICE_STATUS.LOADING) &&
      displayLoaderWhenFetching.current,

    handleFetchMoreTasks,
    handleUpdateFilters,
    handleSearchFilterChange,
    handleExportTasks,
    hasAppliedFilters,
    refreshTasks,
    ...(supportsReordering && { handleReorderTask }),

    SnackbarNotification,
    isNotificationVisible,
    snackbarProps,
  };
};
